/**
 * Subscription Pricing API Client (Dimension-Based Pricing)
 *
 * API functions for dimension-based pricing calculations and config management.
 * Supports both credit-based (Starter) and dimension-based (Pro+) pricing models.
 *
 * Phase 3: Frontend Integration (ADR-010 compliant backend)
 */

import { apiClient } from '../api-client'

// =====================================================
// TYPES
// =====================================================

export interface PricingTier {
  id: string
  dimension_type: 'products' | 'storage_gb'
  tier_order: number
  range_start: number
  range_end: number | null
  price_per_unit: number
  unit_size: number
  display_name: {
    de: string
    en: string
  }
  is_active: boolean
}

export interface PricingPreviewRequest {
  tier_code: 'starter' | 'pro' | 'enterprise'
  pricing_model: 'credit' | 'dimension'
  desired_credits_monthly?: number // Credits per month (all tiers)
  selected_products?: number // For dimension-based (Pro+)
  selected_channels?: number // For dimension-based (Pro+)
}

export interface PricingPreviewResponse {
  monthly_price_eur: number
  annual_price_eur: number
  product_limit: number | null
  storage_limit_bytes: number | null
  channel_count: number | null
  credits_monthly?: number
  breakdown: {
    products?: {
      count: number
      price_eur: number
    }
    storage?: {
      gb: number
      price_eur: number
    }
    channels?: number
  }
  limits: {
    max_products?: number
    max_channels?: number
    max_storage_gb?: number
  }
}

export interface SubscriptionConfig {
  id: string
  status: 'pending' | 'active' | 'canceled'
  pricing_model: 'credit' | 'dimension'
  monthly_price_eur: number
  annual_price_eur: number | null
  product_limit: number | null
  storage_limit_bytes: number | null
  channel_count: number | null
  desired_credits_monthly?: number | null
  current_product_count: number
  current_storage_bytes: number
  pricing_breakdown: Record<string, unknown>
  stripe_subscription_id: string | null
  valid_from: string | null
  valid_until: string | null
}

export interface CreateConfigRequest {
  tier_code: string
  pricing_model: 'credit' | 'dimension'
  desired_credits_monthly?: number
  selected_products?: number
  selected_channels?: number
}

export interface CreateConfigResponse {
  id: string
  status: 'pending'
  monthly_price_eur: number
  annual_price_eur: number | null
  product_limit: number | null
  storage_limit_bytes: number | null
  pricing_breakdown: Record<string, unknown>
}

export interface UsageInfo {
  products: {
    current: number
    limit: number
    percentage: number
  }
  storage: {
    current_bytes: number
    limit_bytes: number
    percentage: number
  }
  channels: {
    current: number
    limit: number
    percentage: number
  }
  credits?: {
    current: number
    limit: number
    percentage: number
  }
}

// =====================================================
// API FUNCTIONS (User-Facing)
// =====================================================

/**
 * Preview pricing without authentication (public calculator)
 */
export async function previewPricing(
  request: PricingPreviewRequest
): Promise<PricingPreviewResponse> {
  const response = await apiClient.post<PricingPreviewResponse>(
    '/api/v2/subscription/pricing/preview',
    request
  )
  return response.data
}

/**
 * Get pricing tiers for dimension (public)
 */
export async function getPricingTiers(
  dimensionType?: 'products' | 'storage_gb'
): Promise<PricingTier[]> {
  const params = dimensionType ? { dimension_type: dimensionType } : {}
  const response = await apiClient.get<{ tiers: PricingTier[] }>(
    '/api/v2/subscription/pricing/tiers',
    { params }
  )
  return response.data.tiers
}

/**
 * Get active subscription config (authenticated)
 */
export async function getSubscriptionConfig(): Promise<SubscriptionConfig> {
  const response = await apiClient.get<SubscriptionConfig>('/api/v2/subscription/pricing/config')
  return response.data
}

/**
 * Create subscription config (authenticated, before checkout)
 */
export async function createSubscriptionConfig(
  request: CreateConfigRequest
): Promise<CreateConfigResponse> {
  const response = await apiClient.post<CreateConfigResponse>(
    '/api/v2/subscription/pricing/config',
    request
  )
  return response.data
}

/**
 * Get current usage vs limits (authenticated)
 */
export async function getUsage(): Promise<UsageInfo> {
  const response = await apiClient.get<UsageInfo>('/api/v2/subscription/pricing/usage')
  return response.data
}

// =====================================================
// API FUNCTIONS (Admin-Only)
// =====================================================

/**
 * Get all pricing tiers (including inactive) - Admin only
 */
export async function getAllPricingTiers(): Promise<PricingTier[]> {
  const response = await apiClient.get<{ tiers: PricingTier[] }>(
    '/api/v2/admin/subscription/pricing/tiers'
  )
  return response.data.tiers
}

/**
 * Create pricing tier - Admin only
 */
export async function createPricingTier(tier: {
  dimension_type: 'products' | 'storage_gb'
  tier_order: number
  range_start: number
  range_end: number | null
  price_per_unit: number
  unit_size: number
  display_name: { de: string; en: string }
}): Promise<PricingTier> {
  const response = await apiClient.post<PricingTier>(
    '/api/v2/admin/subscription/pricing/tiers',
    tier
  )
  return response.data
}

/**
 * Update pricing tier - Admin only
 */
export async function updatePricingTier(
  tierId: string,
  updates: {
    price_per_unit?: number
    is_active?: boolean
    display_name?: { de: string; en: string }
  }
): Promise<PricingTier> {
  const response = await apiClient.put<PricingTier>(
    `/api/v2/admin/subscription/pricing/tiers/${tierId}`,
    updates
  )
  return response.data
}

/**
 * Soft-delete pricing tier (set is_active=false) - Admin only
 */
export async function deletePricingTier(tierId: string): Promise<{ success: boolean }> {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    `/api/v2/admin/subscription/pricing/tiers/${tierId}`
  )
  return { success: response.data.success }
}

/**
 * Admin pricing calculator (for testing) - Admin only
 */
export async function adminCalculator(
  request: PricingPreviewRequest
): Promise<PricingPreviewResponse> {
  const response = await apiClient.post<PricingPreviewResponse>(
    '/api/v2/admin/subscription/pricing/calculator',
    request
  )
  return response.data
}

// =====================================================
// API OBJECT (for hooks)
// =====================================================

/**
 * Subscription Pricing API - Grouped API functions
 *
 * Used by hooks for cleaner imports.
 */
export const subscriptionPricingApi = {
  // User-facing
  preview: previewPricing,
  getTiers: getPricingTiers,
  getConfig: getSubscriptionConfig,
  createConfig: createSubscriptionConfig,
  getUsage: getUsage,

  // Admin-only
  admin: {
    getAllTiers: getAllPricingTiers,
    createTier: createPricingTier,
    updateTier: updatePricingTier,
    deleteTier: deletePricingTier,
    calculator: adminCalculator,
  },
}
